/* eslint-disable react-refresh/only-export-components */
import React from 'react';
import {
  Link,
  useHref,
  useLocation,
  useNavigate,
  type LinkProps,
  type NavigateOptions,
  type To,
} from 'react-router-dom';

import { useAuth } from '../auth/useAuth';
import type { HistoryLocationSnapshot } from './historyLedger';
import { useNavigationHistory } from './NavigationHistoryProvider';
import { isBlockedNavigationFieldName } from './query';
import { isRouteAuthorized } from './routeAuthorization';
import {
  canonicalizeDocumentLocation,
  resolveAppBasename,
  resolveRoutePolicy,
  toDocumentPath,
  toRouterPath,
} from './routePolicy';
import { recordNavigationTelemetry } from './telemetry';
import type { RoutePolicy, RouterBasename } from './types';

export interface NavigationOrigin {
  version: 1;
  basename: RouterBasename;
  scopeId: string;
  routeId: string;
  pathname: string;
  search: string;
  hash: string;
  historyKey: string;
}

export type NavigateWithOrigin = (
  to: To,
  options?: NavigateOptions,
) => void | Promise<void>;

export type OriginLinkProps = LinkProps;

export interface SafeBackButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  fallback?: string;
}

type NavigationMetadata = Record<string, unknown>;

type NonRecordNavigationState =
  | null
  | undefined
  | string
  | number
  | boolean
  | bigint
  | symbol
  | readonly unknown[]
  | ((...args: never[]) => unknown);

type MetadataOnlyNavigationState<TNavigation extends NavigationMetadata> = {
  __aaNavigation: TNavigation;
};

type CallablePropertyKeys<TState extends object> = {
  [TKey in keyof TState]-?: NonNullable<TState[TKey]> extends
    (...args: never[]) => unknown
    ? TKey
    : never;
}[keyof TState];

type IsPreservableNavigationState<TState> =
  TState extends object
    ? TState extends readonly unknown[] | ((...args: never[]) => unknown)
      ? false
      : CallablePropertyKeys<TState> extends never
        ? true
        : false
    : false;

type NavigationMetadataMayBeAbsent<TState extends object> =
  TState extends { __aaNavigation: infer TMetadata }
    ? undefined extends TMetadata
      ? true
      : false
    : true;

type ExistingNavigationMetadata<TState extends object> =
  TState extends { __aaNavigation?: infer TMetadata }
    ? NonNullable<TMetadata> extends object
      ? IsPreservableNavigationState<NonNullable<TMetadata>> extends true
        ? NavigationMetadataMayBeAbsent<TState> extends true
          ? Partial<NonNullable<TMetadata>>
          : NonNullable<TMetadata>
        : Record<never, never>
      : Record<never, never>
    : Record<never, never>;

type MergedRecordNavigationState<
  TState extends object,
  TNavigation extends NavigationMetadata,
> = Omit<TState, '__aaNavigation'> & {
  __aaNavigation: Omit<ExistingNavigationMetadata<TState>, keyof TNavigation>
    & TNavigation;
};

type MergedNavigationState<
  TState extends object,
  TNavigation extends NavigationMetadata,
> = IsPreservableNavigationState<TState> extends true
  ? MergedRecordNavigationState<TState, TNavigation>
  : MetadataOnlyNavigationState<TNavigation>;

type RawLocation = {
  pathname: string;
  search: string;
  hash: string;
};

type TargetValidation =
  | { status: 'invalid' | 'unauthorized' }
  | {
    status: 'valid';
    policy: RoutePolicy;
    documentTo: string;
    routerTo: string;
  };

type OriginCandidate =
  | { present: false }
  | { present: true; value: unknown };

type StoredOriginValidation =
  | { status: 'invalid' | 'unauthorized' }
  | {
    status: 'valid';
    origin: NavigationOrigin;
    target: Extract<TargetValidation, { status: 'valid' }>;
  };

const ROUTER_BASENAMES = new Set<RouterBasename>([
  '/',
  '/manager',
  '/hr',
  '/accountant',
  '/app',
]);

const ORIGIN_KEYS = [
  'version',
  'basename',
  'scopeId',
  'routeId',
  'pathname',
  'search',
  'hash',
  'historyKey',
] as const;

const ORIGIN_KEY_SET = new Set<PropertyKey>(ORIGIN_KEYS);
const FORBIDDEN_SHELLS = new Set(['auth', 'diagnostics', 'error']);
const FORBIDDEN_SUMMER_ROUTE_IDS = new Set([
  'summer.login',
  'summer.create-password',
  'summer.reset-password',
]);
const INVALID_PERCENT_ESCAPE = /%(?![0-9a-f]{2})/i;
const ENCODED_PATH_SEPARATOR = /%(?:2f|3f|23|5c)/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  try {
    const prototype = Object.getPrototypeOf(value) as unknown;
    return prototype === Object.prototype || prototype === null;
  } catch {
    return false;
  }
}

function ownDataProperty(record: object, key: PropertyKey): PropertyDescriptor | undefined {
  try {
    const descriptor = Object.getOwnPropertyDescriptor(record, key);
    return descriptor && 'value' in descriptor ? descriptor : undefined;
  } catch {
    return undefined;
  }
}

function hasControlOrBackslash(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x1f || codePoint === 0x7f || character === '\\') return true;
  }
  return false;
}

export function mergeNavigationState<
  const TNavigation extends NavigationMetadata,
>(
  existingState: NonRecordNavigationState,
  navigationMetadata: TNavigation,
): MetadataOnlyNavigationState<TNavigation>;
export function mergeNavigationState<
  const TState extends object,
  const TNavigation extends NavigationMetadata,
>(
  existingState: TState,
  navigationMetadata: TNavigation,
): MergedNavigationState<TState, TNavigation>;
export function mergeNavigationState<
  const TNavigation extends NavigationMetadata,
>(
  existingState: unknown,
  navigationMetadata: TNavigation,
): MetadataOnlyNavigationState<TNavigation>;
export function mergeNavigationState(
  existingState: unknown,
  navigationMetadata: NavigationMetadata,
): Record<string, unknown> & { __aaNavigation: NavigationMetadata } {
  const state = isRecord(existingState) ? { ...existingState } : {};
  const currentMetadata = isRecord(state.__aaNavigation)
    ? state.__aaNavigation
    : {};

  return {
    ...state,
    __aaNavigation: {
      ...currentMetadata,
      ...navigationMetadata,
    },
  };
}

export function navigateWithOriginState(
  existingState: NonRecordNavigationState,
  origin: NavigationOrigin,
): MetadataOnlyNavigationState<{ origin: NavigationOrigin }>;
export function navigateWithOriginState<
  const TState extends object,
>(
  existingState: TState,
  origin: NavigationOrigin,
): MergedNavigationState<TState, { origin: NavigationOrigin }>;
export function navigateWithOriginState(
  existingState: unknown,
  origin: NavigationOrigin,
): MetadataOnlyNavigationState<{ origin: NavigationOrigin }>;
export function navigateWithOriginState(
  existingState: unknown,
  origin: NavigationOrigin,
): Record<string, unknown> & { __aaNavigation: NavigationMetadata & { origin: NavigationOrigin } } {
  return mergeNavigationState(existingState, { origin }) as Record<string, unknown> & {
    __aaNavigation: NavigationMetadata & { origin: NavigationOrigin };
  };
}

function splitRawLocation(value: string): RawLocation | null {
  const hashIndex = value.indexOf('#');
  const beforeHash = hashIndex === -1 ? value : value.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : value.slice(hashIndex);
  const searchIndex = beforeHash.indexOf('?');
  const pathname = searchIndex === -1
    ? beforeHash
    : beforeHash.slice(0, searchIndex);
  const search = searchIndex === -1 ? '' : beforeHash.slice(searchIndex);
  return pathname.length > 0 ? { pathname, search, hash } : null;
}

function hasSafeEncoding(value: string): boolean {
  if (hasControlOrBackslash(value) || INVALID_PERCENT_ESCAPE.test(value)) return false;
  try {
    const decoded = decodeURIComponent(value);
    if (hasControlOrBackslash(decoded)) return false;
    encodeURI(value);
    return true;
  } catch {
    return false;
  }
}

function hasSafeDocumentComponents(location: RawLocation): boolean {
  const { pathname, search, hash } = location;
  if (!pathname.startsWith('/') || pathname.startsWith('//')) return false;
  if (pathname.includes('?') || pathname.includes('#') || pathname.includes('://')) return false;
  if (ENCODED_PATH_SEPARATOR.test(pathname)) return false;
  if (search !== '' && (!search.startsWith('?') || search.includes('#'))) return false;
  if (hash !== '' && !hash.startsWith('#')) return false;
  return hasSafeEncoding(pathname)
    && hasSafeEncoding(search)
    && hasSafeEncoding(hash);
}

function hasBlockedQueryField(search: string): boolean {
  try {
    for (const key of new URLSearchParams(search).keys()) {
      if (isBlockedNavigationFieldName(key)) return true;
    }
    return false;
  } catch {
    return true;
  }
}

function isForbiddenPolicy(policy: RoutePolicy): boolean {
  if (FORBIDDEN_SHELLS.has(policy.shell)) return true;
  if (FORBIDDEN_SUMMER_ROUTE_IDS.has(policy.id)) return true;
  if (/(?:^|\/)logout(?:\/|$)/i.test(policy.routerPath)
    || /(?:^|[.])logout(?:[.]|$)/i.test(policy.id)) return true;
  return policy.routerPath
    .split('/')
    .some((segment) => segment.toLowerCase().replace(/[?*]+$/, '') === ':token');
}

function validateDocumentTarget({
  location,
  runtimeBasename,
  user,
  expectedRouteId,
}: {
  location: RawLocation;
  runtimeBasename: RouterBasename;
  user: ReturnType<typeof useAuth>['user'];
  expectedRouteId?: string;
}): TargetValidation {
  if (!hasSafeDocumentComponents(location)) return { status: 'invalid' };
  if (resolveAppBasename(location.pathname) !== runtimeBasename) {
    return { status: 'invalid' };
  }
  if (hasBlockedQueryField(location.search)) return { status: 'invalid' };

  const documentTo = `${location.pathname}${location.search}${location.hash}`;
  try {
    if (canonicalizeDocumentLocation(documentTo) !== documentTo) {
      return { status: 'invalid' };
    }
  } catch {
    return { status: 'invalid' };
  }

  const routerTo = toRouterPath(runtimeBasename, documentTo);
  const policy = resolveRoutePolicy({
    basename: runtimeBasename,
    pathname: toRouterPath(runtimeBasename, location.pathname),
  });
  if (!policy || isForbiddenPolicy(policy)) return { status: 'invalid' };
  if (expectedRouteId !== undefined && policy.id !== expectedRouteId) {
    return { status: 'invalid' };
  }
  if (!isRouteAuthorized(policy, user)) return { status: 'unauthorized' };
  return { status: 'valid', policy, documentTo, routerTo };
}

function isSafeOpaqueValue(value: unknown, maxLength: number): value is string {
  return typeof value === 'string'
    && value.length > 0
    && value.length <= maxLength
    && !hasControlOrBackslash(value);
}

function parseNavigationOrigin(value: unknown): NavigationOrigin | null {
  if (!isRecord(value)) return null;
  let keys: PropertyKey[];
  try {
    keys = Reflect.ownKeys(value);
  } catch {
    return null;
  }
  if (keys.length !== ORIGIN_KEYS.length
    || keys.some((key) => !ORIGIN_KEY_SET.has(key))) return null;

  const values = Object.create(null) as Record<PropertyKey, unknown>;
  for (const key of ORIGIN_KEYS) {
    const descriptor = ownDataProperty(value, key);
    if (!descriptor) return null;
    values[key] = descriptor.value;
  }

  if (values.version !== 1
    || !ROUTER_BASENAMES.has(values.basename as RouterBasename)
    || !isSafeOpaqueValue(values.scopeId, 512)
    || !isSafeOpaqueValue(values.routeId, 256)
    || typeof values.pathname !== 'string'
    || typeof values.search !== 'string'
    || typeof values.hash !== 'string'
    || !isSafeOpaqueValue(values.historyKey, 256)) return null;

  return {
    version: 1,
    basename: values.basename as RouterBasename,
    scopeId: values.scopeId,
    routeId: values.routeId,
    pathname: values.pathname,
    search: values.search,
    hash: values.hash,
    historyKey: values.historyKey,
  };
}

function readOriginCandidate(state: unknown): OriginCandidate {
  if (!isRecord(state)) return { present: false };
  const navigationDescriptor = ownDataProperty(state, '__aaNavigation');
  if (!navigationDescriptor || !isRecord(navigationDescriptor.value)) {
    return { present: false };
  }
  const originDescriptor = ownDataProperty(navigationDescriptor.value, 'origin');
  return originDescriptor
    ? { present: true, value: originDescriptor.value }
    : { present: false };
}

function validateStoredOrigin({
  value,
  runtimeBasename,
  scopeId,
  currentHistoryKey,
  historyCursor,
  historyEntries,
  user,
}: {
  value: unknown;
  runtimeBasename: RouterBasename;
  scopeId: string;
  currentHistoryKey: string;
  historyCursor: number;
  historyEntries: readonly HistoryLocationSnapshot[];
  user: ReturnType<typeof useAuth>['user'];
}): StoredOriginValidation {
  const origin = parseNavigationOrigin(value);
  if (!origin
    || origin.basename !== runtimeBasename
    || origin.scopeId !== scopeId
    || origin.historyKey === currentHistoryKey) return { status: 'invalid' };

  const target = validateDocumentTarget({
    location: origin,
    runtimeBasename,
    user,
    expectedRouteId: origin.routeId,
  });
  if (target.status !== 'valid') return target;
  const knownEntryIndex = historyEntries.findIndex(
    (entry) => entry.key === origin.historyKey,
  );
  if (knownEntryIndex !== -1 && knownEntryIndex >= historyCursor) {
    return { status: 'invalid' };
  }
  const knownEntry = historyEntries[knownEntryIndex];
  if (knownEntryIndex !== -1 && knownEntry
    && (toDocumentPath(runtimeBasename, knownEntry.pathname) !== origin.pathname
      || knownEntry.search !== origin.search
      || knownEntry.hash !== origin.hash)) return { status: 'invalid' };
  return { status: 'valid', origin, target };
}

function validateRouterFallback({
  fallback,
  runtimeBasename,
  user,
}: {
  fallback: string | undefined;
  runtimeBasename: RouterBasename;
  user: ReturnType<typeof useAuth>['user'];
}): Extract<TargetValidation, { status: 'valid' }> | null {
  if (fallback === undefined) return null;
  const routerLocation = splitRawLocation(fallback);
  if (!routerLocation || !hasSafeDocumentComponents(routerLocation)) return null;

  const documentLocation: RawLocation = {
    pathname: toDocumentPath(runtimeBasename, routerLocation.pathname),
    search: routerLocation.search,
    hash: routerLocation.hash,
  };
  const target = validateDocumentTarget({
    location: documentLocation,
    runtimeBasename,
    user,
  });
  return target.status === 'valid' ? target : null;
}

function useRuntimeBasename(): RouterBasename {
  return resolveAppBasename(useHref('/'));
}

function useCurrentNavigationOrigin(): NavigationOrigin | null {
  const location = useLocation();
  const history = useNavigationHistory();
  const { user } = useAuth();
  const basename = useRuntimeBasename();

  return React.useMemo(() => {
    if (history.location.key !== location.key
      || history.location.pathname !== location.pathname
      || history.location.search !== location.search
      || history.location.hash !== location.hash
      || !isSafeOpaqueValue(history.scopeId, 512)
      || !isSafeOpaqueValue(location.key, 256)) return null;

    const documentLocation: RawLocation = {
      pathname: toDocumentPath(basename, location.pathname),
      search: location.search,
      hash: location.hash,
    };
    const target = validateDocumentTarget({
      location: documentLocation,
      runtimeBasename: basename,
      user,
    });
    if (target.status !== 'valid') return null;

    return Object.freeze({
      version: 1 as const,
      basename,
      scopeId: history.scopeId,
      routeId: target.policy.id,
      pathname: documentLocation.pathname,
      search: documentLocation.search,
      hash: documentLocation.hash,
      historyKey: location.key,
    });
  }, [
    basename,
    history.location.hash,
    history.location.key,
    history.location.pathname,
    history.location.search,
    history.scopeId,
    location.hash,
    location.key,
    location.pathname,
    location.search,
    user,
  ]);
}

export function useNavigateWithOrigin(): NavigateWithOrigin {
  const navigate = useNavigate();
  const origin = useCurrentNavigationOrigin();
  return React.useCallback((to: To, options?: NavigateOptions) => {
    if (!origin) return navigate(to, options);
    return navigate(to, {
      ...options,
      state: navigateWithOriginState(options?.state, origin),
    });
  }, [navigate, origin]);
}

export const OriginLink = React.forwardRef<HTMLAnchorElement, OriginLinkProps>(
  function OriginLink({ state, ...props }, ref) {
    const origin = useCurrentNavigationOrigin();
    return (
      <Link
        {...props}
        ref={ref}
        state={origin ? navigateWithOriginState(state, origin) : state}
      />
    );
  },
);

export function useHistoryPop(): () => void {
  const navigate = useNavigate();
  return React.useCallback(() => {
    void navigate(-1);
  }, [navigate]);
}

function useSafeBackAction(fallback?: string): {
  back: () => void;
  replace: () => void;
  canNavigate: boolean;
} {
  const location = useLocation();
  const history = useNavigationHistory();
  const { user } = useAuth();
  const runtimeBasename = useRuntimeBasename();
  const navigate = useNavigate();
  const popOneHistoryEntry = useHistoryPop();

  const currentPolicy = React.useMemo(() => resolveRoutePolicy({
    basename: runtimeBasename,
    pathname: location.pathname,
  }), [location.pathname, runtimeBasename]);
  const originCandidate = React.useMemo(
    () => readOriginCandidate(location.state),
    [location.state],
  );
  const origin = React.useMemo<StoredOriginValidation | null>(
    () => originCandidate.present
      ? validateStoredOrigin({
        value: originCandidate.value,
        runtimeBasename,
        scopeId: history.scopeId,
        currentHistoryKey: history.location.key,
        historyCursor: history.cursor,
        historyEntries: history.entries,
        user,
      })
      : null,
    [
      history.entries,
      history.cursor,
      history.location.key,
      history.scopeId,
      originCandidate,
      runtimeBasename,
      user,
    ],
  );
  const callerFallback = React.useMemo(
    () => validateRouterFallback({ fallback, runtimeBasename, user }),
    [fallback, runtimeBasename, user],
  );
  const registeredFallback = React.useMemo(
    () => validateRouterFallback({
      fallback: currentPolicy?.fallbackRouterPath,
      runtimeBasename,
      user,
    }),
    [currentPolicy?.fallbackRouterPath, runtimeBasename, user],
  );
  const replacement = origin?.status === 'valid'
    ? origin.target
    : callerFallback ?? registeredFallback;

  const back = React.useCallback(() => {
    if (history.canGoBack) {
      popOneHistoryEntry();
      return;
    }

    if (originCandidate.present && origin?.status !== 'valid') {
      recordNavigationTelemetry({
        name: 'origin_fallback',
        ...(currentPolicy
          ? { routeId: currentPolicy.id, shell: currentPolicy.shell }
          : {}),
        reason: origin?.status === 'unauthorized'
          ? 'unauthorized-origin'
          : 'invalid-origin',
      });
    }

    if (replacement) {
      void navigate(replacement.routerTo, { replace: true });
    }
  }, [
    currentPolicy,
    history.canGoBack,
    navigate,
    origin?.status,
    originCandidate.present,
    popOneHistoryEntry,
    replacement,
  ]);

  const replace = React.useCallback(() => {
    if (originCandidate.present && origin?.status !== 'valid') {
      recordNavigationTelemetry({
        name: 'origin_fallback',
        ...(currentPolicy
          ? { routeId: currentPolicy.id, shell: currentPolicy.shell }
          : {}),
        reason: origin?.status === 'unauthorized'
          ? 'unauthorized-origin'
          : 'invalid-origin',
      });
    }

    if (replacement) {
      void navigate(replacement.routerTo, { replace: true });
    }
  }, [
    currentPolicy,
    navigate,
    origin?.status,
    originCandidate.present,
    replacement,
  ]);

  return {
    back,
    replace,
    canNavigate: history.canGoBack || replacement !== null,
  };
}

export function useSafeBack(fallback?: string): () => void {
  return useSafeBackAction(fallback).back;
}

export function useSafeReplace(fallback?: string): () => void {
  return useSafeBackAction(fallback).replace;
}

export function SafeBackButton({
  fallback,
  type = 'button',
  disabled,
  onClick,
  ...buttonProps
}: SafeBackButtonProps) {
  const { back, canNavigate } = useSafeBackAction(fallback);
  const isDisabled = disabled === true || !canNavigate;
  const handleClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    if (!event.defaultPrevented) back();
  }, [back, onClick]);

  return (
    <button
      {...buttonProps}
      type={type}
      disabled={isDisabled}
      onClick={handleClick}
    />
  );
}
